import { loadBaseCSS } from '../shared/button-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import { numberAttribute, primitiveEvent, primitiveStyles } from '../shared/primitive-utils.js';
import { sanitizeUrl } from '../shared/url-utils.js';

export interface BreadcrumbItem { label: string; href?: string; }
let breadcrumbId = 0;

/**
 * Hierarchical native navigation with a current page and expandable middle ancestors.
 * @element qds-breadcrumb
 * @attr {string} label - Accessible navigation name. Default Breadcrumb.
 * @attr {number} max-items - Visible ancestors/current item before overflow. Minimum 2, default 4.
 * @attr {boolean} expanded - Reveal collapsed middle ancestors.
 * @property {Array<{label: string; href?: string}>} items - Property-only ordered path. Last item is the current page.
 * @property {string} label - Navigation landmark name.
 * @property {number} maxItems - Visible path item limit, excluding the overflow button.
 * @property {boolean} expanded - Controlled overflow expansion.
 * @fires {{expanded: boolean; component: HTMLElement}} qds-toggle - User expands/collapses ancestors.
 * @fires {{index: number; item: {label: string; href?: string}; component: HTMLElement}} qds-navigate - Cancelable user navigation; prevent default to delegate routing.
 * @csspart navigation - Named navigation landmark.
 * @csspart list - Ordered path.
 * @csspart link - Native ancestor link.
 * @csspart current - Current-page text.
 * @csspart overflow - Expand/collapse button.
 */
export class QdsBreadcrumb extends HTMLElement {
  static get observedAttributes(): string[] { return ['label', 'max-items', 'expanded']; }
  private _items: BreadcrumbItem[] = [];
  private _id = `qds-breadcrumb-${++breadcrumbId}`;
  private _onClick = (event: Event): void => {
    const target = (event.target as Node | null)?.nodeType === Node.ELEMENT_NODE ? event.target as Element : null;
    if (target?.closest('button[data-overflow]')) {
      this.expanded = !this.expanded;
      primitiveEvent(this, 'qds-toggle', { expanded: this.expanded, component: this });
      return;
    }
    const anchor = target?.closest<HTMLAnchorElement>('a[data-index]');
    if (!anchor) return;
    const index = Number(anchor.dataset.index);
    if (!primitiveEvent(this, 'qds-navigate', { index, item: { ...this._items[index] }, component: this }, true)) event.preventDefault();
  };
  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}
      :host { display: block; }
      ol { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; list-style: none; padding: 0; margin: 0; }
      li { display: inline-flex; align-items: center; min-width: 0; gap: 4px; }
      a, button { display: inline-flex; align-items: center; justify-content: center; min-height: 44px; min-width: 44px;
        padding: 8px; border-radius: 4px; color: var(--theme-text-primary, #333); overflow-wrap: anywhere; }
      a { text-underline-offset: .15em; }
      button { border: 1px solid var(--theme-border-primary, #e0e0e0); background: var(--theme-background-primary, white); cursor: pointer; }
      a:hover, button:hover { background: var(--theme-background-hover, rgba(0,0,0,.05)); }
      a:focus-visible, nav:focus-visible { outline: 2px solid var(--theme-accent-primary, #445e89); outline-offset: 2px; }
      [part=current] { padding: 8px; overflow-wrap: anywhere; }
      .divider { padding: 0 4px; }
    </style><nav part="navigation" tabindex="-1"><ol part="list"></ol></nav>`;
    loadBaseCSS();
  }
  connectedCallback(): void {
    for (const name of ['items', 'label', 'maxItems', 'expanded']) upgradeProperty(this, name);
    this.shadowRoot!.addEventListener('click', this._onClick); this._render();
  }
  disconnectedCallback(): void { this.shadowRoot!.removeEventListener('click', this._onClick); }
  attributeChangedCallback(): void { if (this.isConnected) this._render(); }
  get items(): BreadcrumbItem[] { return this._items.map(item => ({ ...item })); }
  set items(value: ReadonlyArray<BreadcrumbItem>) {
    this._items = Array.isArray(value) ? value.flatMap(item => {
      if (!item || typeof item.label !== 'string' || !item.label.trim()) return [];
      const href = this._safeHref(item.href);
      return [{ label: item.label.trim(), ...(href ? { href } : {}) }];
    }) : [];
    this._render();
  }
  get label(): string { return this.getAttribute('label') || 'Breadcrumb'; }
  set label(value: string) { this.setAttribute('label', value ?? ''); }
  get maxItems(): number { return Math.max(2, Math.trunc(numberAttribute(this, 'max-items', 4))); }
  set maxItems(value: number) { this.setAttribute('max-items', String(value)); }
  get expanded(): boolean { return this.hasAttribute('expanded'); }
  set expanded(value: boolean) { this.toggleAttribute('expanded', !!value); }
  private _safeHref(value: unknown): string {
    const candidate = sanitizeUrl(value);
    if (!candidate) return '';
    try { return ['http:', 'https:'].includes(new URL(candidate, document.baseURI).protocol) ? candidate : ''; }
    catch { return ''; }
  }
  private _render(): void {
    const nav = this.shadowRoot!.querySelector<HTMLElement>('nav')!;
    const list = this.shadowRoot!.querySelector('ol')!;
    const active = this.shadowRoot!.activeElement as HTMLElement | null;
    nav.setAttribute('aria-label', this.label); nav.hidden = !this._items.length;
    const overflow = this._items.length > this.maxItems;
    const tail = this._items.length - this.maxItems + 1;
    const middleIds = this._items.map((_, index) => `${this._id}-${index}`).slice(1, tail);
    const nodes: HTMLElement[] = [];
    const divider = (): HTMLSpanElement => { const span = document.createElement('span'); span.className = 'divider'; span.setAttribute('aria-hidden', 'true'); span.textContent = '/'; return span; };
    this._items.forEach((item, index) => {
      if (overflow && index === 1) {
        const li = document.createElement('li'); li.append(divider());
        const button = document.createElement('button'); button.type = 'button'; button.setAttribute('part', 'overflow'); button.dataset.overflow = '';
        button.setAttribute('aria-expanded', String(this.expanded)); button.setAttribute('aria-controls', middleIds.join(' '));
        button.setAttribute('aria-label', this.expanded ? 'Hide ancestors' : 'Show hidden ancestors'); button.textContent = this.expanded ? '−' : '…'; li.append(button); nodes.push(li);
      }
      const li = document.createElement('li'); li.id = `${this._id}-${index}`;
      li.hidden = overflow && index > 0 && index < tail && !this.expanded;
      if (index) li.append(divider());
      const current = index === this._items.length - 1;
      const content = document.createElement(!current && item.href ? 'a' : 'span'); content.textContent = item.label;
      if (content instanceof HTMLAnchorElement) { content.href = item.href!; content.dataset.index = String(index); content.setAttribute('part', 'link'); }
      if (current) { content.setAttribute('aria-current', 'page'); content.setAttribute('part', 'current'); }
      li.append(content); nodes.push(li);
    });
    list.replaceChildren(...nodes);
    if (active && this._items.length) {
      const replacement = active.hasAttribute('data-overflow') ? list.querySelector<HTMLElement>('button') : list.querySelector<HTMLElement>(`a[data-index="${active.dataset.index}"]`);
      const fallback = list.querySelector<HTMLElement>('button') || [...list.querySelectorAll('a')].find(link => !link.closest('[hidden]')) || nav;
      (replacement && !replacement.closest('[hidden]') ? replacement : fallback).focus();
    }
  }
}
if (!customElements.get('qds-breadcrumb')) customElements.define('qds-breadcrumb', QdsBreadcrumb);
